import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

type SidebarItem = {
  path: string;
  label: string;
  description: string;
  icon: ReactNode;
  divider?: boolean;
  disabled?: boolean;
  badge?: string;
  disabledReason?: string;
  hidePromptText?: boolean;
};

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  items: SidebarItem[];
  activePath: string;
  promptsByPath?: Record<string, string>;
  resumeSupplement?: {
    label: string;
    onClick: () => void;
    afterPath: string;
    hint?: string;
  };
};

export default function Sidebar({
  collapsed,
  onToggle,
  items,
  activePath,
  promptsByPath,
  resumeSupplement,
}: SidebarProps) {
  const { user, logout } = useAuth();
  const sidebarMotionMs = 1400;
  const [showText, setShowText] = useState(!collapsed);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (collapsed) {
      setShowText(false);
    } else {
      timer = setTimeout(() => setShowText(true), sidebarMotionMs);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [collapsed, sidebarMotionMs]);

  const logoutIcon = (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
    </svg>
  );

  return (
    <aside
      className={`flex min-h-screen flex-col border-r border-transparent text-white shadow-xl transition-[width,background-color,box-shadow] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)] ${
        collapsed ? "w-16" : "w-64"
      }`}
      style={{
        backgroundColor: "#000428",
        backgroundImage:
          "linear-gradient(180deg, rgba(0,41,75,0.92), rgba(0,41,75,0.85)), url('/tint-sidebar.png')",
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "repeat-y",
      }}
    >
      <div className="flex h-28 flex-col justify-end px-4 pb-4">
        <div
          className={`origin-left transition-[opacity,transform] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)] ${
            collapsed ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100"
          }`}
        >
          <img
            src="/logo.png"
            alt="ClaimSupplement"
            className="w-full object-contain"
            draggable={false}
          />
        </div>
      </div>
      <div className="flex items-center justify-end px-4 pb-4">
        <button
          type="button"
          onClick={onToggle}
          className="rounded-full bg-white/5 p-2 text-xs text-slate-300 transition hover:bg-white/10"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? "›" : "‹"}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-6">
        <nav className="space-y-2" aria-label="Primary navigation">
          {items.map((item) => {
            if (item.divider) {
              return (
                <div
                  key={item.path}
                  className="mx-1 my-3 border-t-2 border-white/20"
                  aria-hidden="true"
                />
              );
            }

            const prompt = promptsByPath?.[item.path];
            const isActive =
              activePath === item.path || activePath.startsWith(`${item.path}/`);
            const disabledReason =
              item.disabled && (item.disabledReason || "Upload both estimates to unlock");
            const itemClassName = [
              "group flex items-start gap-3 rounded-2xl px-4 py-3 transition-all duration-200",
              isActive
                ? "bg-white/10 shadow-lg shadow-teal-500/10"
                : item.disabled
                  ? "cursor-not-allowed text-slate-400 opacity-80 hover:bg-white/5 hover:text-slate-100"
                  : "text-slate-300 hover:bg-white/5",
            ].join(" ");
            const iconClassName = [
              "relative -mt-0.5 flex h-5 w-5 items-center justify-center text-white transition-transform duration-200 ease-out",
              item.disabled ? "group-hover:ring-1 group-hover:ring-white/20" : "group-hover:-translate-y-1",
            ].join(" ");
            const itemBody = (
              <>
                <span className={iconClassName} aria-hidden="true">
                  {item.icon}
                  {prompt && collapsed && (
                    <span
                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#38bdf8] shadow-[0_0_8px_rgba(56,189,248,0.75)]"
                      aria-label={prompt}
                      title={prompt}
                    />
                  )}
                </span>
                {showText && (
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-semibold">{item.label}</div>
                      {item.badge && (
                        <span className="rounded-full border border-[#38bdf8]/60 bg-[#38bdf8]/15 px-2 py-0.5 text-[10px] font-semibold text-[#38bdf8]">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">{item.description}</p>
                    {disabledReason && (
                      <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400/80">
                        {disabledReason}
                      </div>
                    )}
                    {prompt && !item.hidePromptText && (
                      <div className="mt-1 inline-flex max-w-[210px] items-center rounded-full border border-[#38bdf8]/60 bg-[#38bdf8]/15 px-2 py-0.5 text-[9px] font-semibold text-[#38bdf8] shadow-[0_0_12px_rgba(56,189,248,0.45)]">
                        {prompt}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
            return (
              <div key={item.path} className="space-y-2">
                {item.disabled ? (
                  <div
                    className={itemClassName}
                    aria-disabled="true"
                    role="link"
                    title={disabledReason || undefined}
                  >
                    {itemBody}
                  </div>
                ) : (
                  <NavLink to={item.path} className={itemClassName}>
                    {itemBody}
                  </NavLink>
                )}
                {resumeSupplement && resumeSupplement.afterPath === item.path ? (
                  <button
                    type="button"
                    onClick={resumeSupplement.onClick}
                    className={[
                      "group relative overflow-hidden rounded-2xl border px-4 py-3 text-left text-[10px] font-semibold transition",
                      "border-[#38bdf8]/60 bg-[#38bdf8]/15 text-[#38bdf8]",
                      "shadow-[0_0_12px_rgba(56,189,248,0.35)] hover:border-[#38bdf8]",
                      "hover:bg-[#38bdf8]/20 animate-[pulse_3.6s_ease-in-out_infinite]",
                      collapsed ? "flex h-9 w-9 items-center justify-center" : "w-full",
                    ].join(" ")}
                    aria-label={resumeSupplement.label}
                    title={resumeSupplement.label}
                  >
                    <span
                      className={[
                        "inline-flex items-center gap-2",
                        collapsed ? "" : "text-[10px] uppercase tracking-[0.2em]",
                      ].join(" ")}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                      {showText && <span>{resumeSupplement.label}</span>}
                    </span>
                    {showText && resumeSupplement.hint && (
                      <div className="mt-1 text-[9px] font-normal text-[#38bdf8]/80">
                        {resumeSupplement.hint}
                      </div>
                    )}
                  </button>
                ) : null}
              </div>
            );
          })}
        </nav>
      </div>
      <div className="border-t border-white/5 px-4 py-4 text-xs text-slate-500">
        {showText && (
          <div className="mb-2 truncate text-sm font-semibold text-slate-100">
            {user?.email || "Not signed in"}
          </div>
        )}
        <button
          type="button"
          onClick={logout}
          className="group flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-[10px] font-semibold text-slate-100 hover:bg-white/10"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-slate-100 transition-transform duration-200 ease-out group-hover:-translate-y-1">
            {logoutIcon}
          </span>
          {showText && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
}
